import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { MyContext } from '../../../context';
import DataTable from '../../../components/tables/DataTable';
import Pagination from '../../../components/Pagination';
import DialogModal from '../../../components/dialog';
import { listWorkflowApprovals, approveWorkflowApproval, rejectWorkflowApproval } from '../../../utils/queries';

const PAGE_SIZE = 20;

export default function WorkflowApprovalsList() {
	const { activeStoreId } = useContext(MyContext);
	const [approvals, setApprovals] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [loading, setLoading] = useState(true);

	const refresh = () => {
		if (!activeStoreId) return;
		setLoading(true);
		listWorkflowApprovals({ storeId: activeStoreId, limit: PAGE_SIZE, offset: page * PAGE_SIZE }).then((result) => {
			if (result?.success) {
				setApprovals(result.data.approvals);
				setTotal(result.data.pagination.total);
			}
			setLoading(false);
		});
	};

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, activeStoreId]);

	const [approveTarget, setApproveTarget] = useState(null);
	const [approveWorking, setApproveWorking] = useState(false);
	const handleApproveConfirm = async () => {
		if (!approveTarget) return;
		setApproveWorking(true);
		const result = await approveWorkflowApproval(approveTarget.workflow_approval_id, activeStoreId);
		setApproveWorking(false);
		setApproveTarget(null);
		if (!result?.success) {
			toast.error(result?.error?.message || 'Failed to approve.');
			return;
		}
		toast.success('Approved.');
		refresh();
	};

	const [declineTarget, setDeclineTarget] = useState(null);
	const [declineReason, setDeclineReason] = useState('');
	const [declineWorking, setDeclineWorking] = useState(false);
	const openDecline = (row) => {
		setDeclineTarget(row);
		setDeclineReason('');
	};
	const handleDeclineConfirm = async () => {
		if (!declineTarget) return;
		setDeclineWorking(true);
		const result = await rejectWorkflowApproval(declineTarget.workflow_approval_id, activeStoreId, declineReason.trim());
		setDeclineWorking(false);
		if (!result?.success) {
			toast.error(result?.error?.message || 'Failed to decline.');
			return;
		}
		toast.success('Declined.');
		setDeclineTarget(null);
		setDeclineReason('');
		refresh();
	};

	const columns = [
		{
			key: 'workflow_name',
			header: 'Workflow',
			render: (value, row) => (
				<Link to={`/workflows/runs/${row.workflow_instance_id}`} className='text-black font-medium hover:underline'>
					{value || `Workflow #${row.workflow_instance_id}`}
				</Link>
			),
		},
		{ key: 'entity_id', header: 'Entity', render: (value, row) => `${row.entity_type} #${value}` },
		{ key: 'approver_role', header: 'Approver', render: (value, row) => value || (row.approver_user_id ? `User #${row.approver_user_id}` : '—') },
		{ key: 'requested_at', header: 'Requested', render: (value) => (value ? new Date(value).toLocaleString() : '-') },
		{
			key: 'workflow_approval_id',
			header: '',
			sortable: false,
			render: (_value, row) => (
				<div className='flex gap-2 justify-end'>
					<button type='button' className='btn-secondary-small' onClick={() => openDecline(row)}>
						Decline
					</button>
					<button type='button' className='btn-primary-small' onClick={() => setApproveTarget(row)}>
						Approve
					</button>
				</div>
			),
		},
	];

	return (
		<div className='py-8 pr-8'>
			<h1 className='text-2xl font-light tracking-tight mb-6'>Workflow Approvals</h1>

			<DataTable columns={columns} data={approvals} loading={loading} emptyHeading='No workflows waiting on approval' />
			<Pagination pageCount={Math.ceil(total / PAGE_SIZE)} currentPage={page} onPageChange={setPage} />

			<DialogModal open={!!approveTarget} setOpen={(open) => !open && setApproveTarget(null)} title='Approve this step?'>
				<p className='text-sm text-gray-600'>The workflow will resume along its "approved" path. This cannot be undone.</p>
				<div className='flex justify-end gap-2 pt-2'>
					<button type='button' className='btn-secondary' onClick={() => setApproveTarget(null)}>
						Back
					</button>
					<button type='button' className='btn-primary' disabled={approveWorking} onClick={handleApproveConfirm}>
						{approveWorking ? 'Approving...' : 'Approve'}
					</button>
				</div>
			</DialogModal>

			<DialogModal open={!!declineTarget} setOpen={(open) => !open && setDeclineTarget(null)} title='Decline this step?'>
				<p className='text-sm text-gray-600'>The workflow will resume along its "rejected" path.</p>
				<div className='flex flex-col'>
					<label className='text-xs text-gray-500'>Comments (optional)</label>
					<textarea className='form-input mt-2' rows={3} value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} />
				</div>
				<div className='flex justify-end gap-2 pt-2'>
					<button type='button' className='btn-secondary' onClick={() => setDeclineTarget(null)}>
						Back
					</button>
					<button type='button' className='btn-alert' disabled={declineWorking} onClick={handleDeclineConfirm}>
						{declineWorking ? 'Declining...' : 'Decline'}
					</button>
				</div>
			</DialogModal>
		</div>
	);
}
